import { REPORT } from '@consts/reports';
import { useReportLoad } from '@hooks/useReportLoad';
import { BaseReport } from '../BaseReport';
import { getUniqueDates, groupByKeys } from '@utils/data';
import { average, countUnique } from '@utils/summaries';
import { basicSort, dateSort } from '@utils/sort';
import { useRange } from '@hooks/useRange';
import { chart } from '@utils/charts';
import { styleDate, styleDateRange } from '@utils/styles';

interface AverageCutsByResellerProps {
    dateRange: { startDate: string; endDate: string };
}

export const AverageCutsByReseller = ({ dateRange }: AverageCutsByResellerProps) => {
    const [range, setRange] = useRange('AverageCutsByReseller');

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.RESELLER_AVERAGE_CUTS_PER_LOCATION_PER_RESELLER,
        params: { ...dateRange, range },
    });

    const regions = groupByKeys(data, ['regionName', 'date']);

    const resellers = groupByKeys(data, ['resellerName', 'date']);

    const dateKeys = getUniqueDates(data, range);

    const regionSeries = Object.keys(regions).flatMap((region: string) => ({
        visible: true,
        name: `Region: ${region}`,
        data: dateKeys.map(({ date }) => regions[region]?.[date]?.regionAvg || 0),
    }));

    const resellerSeries = Object.keys(resellers).flatMap((reseller: string) => ({
        visible: false,
        name: reseller,
        data: dateKeys.map(({ date }) => resellers[reseller]?.[date]?.avgCuts || 0),
    }));

    return (
        <BaseReport
            title={'Average Cuts per Location by Reseller'}
            description={
                'This graph shows the average cuts per location each of your Resellers do, by month, and allows you to compare them to the average of various regions. For example, if a Reseller has 5 total locations, and they performed a total of 400 cuts, their average cuts per location would be 80. Only successful, new sale cuts are measured.'
            }
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            loading={loading}
            hasData={hasData}
            range={range}
            setRange={setRange}
            tableOptions={{
                headers: ['Date', 'Reseller Name', 'Avg Successful Cuts', 'Region Name', 'Region Avg'],
                data: data.map((item) => [item.date, item.resellerName, item.avgCuts, item.regionName, item.regionAvg]),
                summaries: [countUnique, countUnique, average, countUnique, average],
                style: [styleDate(range)],
                sort: [dateSort(range), basicSort, basicSort, basicSort, basicSort],
            }}
            chartOptions={chart(
                500,
                [
                    { name: 'Reseller Average', opposite: false },
                    { name: 'Region Average', opposite: true },
                ],
                dateKeys,
                [...regionSeries, ...resellerSeries]
            )}
        />
    );
};
